import React, { useState, useRef } from 'react'
import {
  uploadService,
  isValidVideoFile,
  isValidDocumentFile,
  isValidImageFile,
  MAX_VIDEO_SIZE,
  MAX_DOCUMENT_SIZE,
  MAX_IMAGE_SIZE,
  formatFileSize,
  type UploadProgress,
} from '@/services/uploadService'

interface FileUploadProps {
  type: 'video' | 'document' | 'image'
  currentUrl?: string
  onUpload: (url: string) => void
  label?: string
  accept?: string
}

export const FileUpload: React.FC<FileUploadProps> = ({
  type,
  currentUrl,
  onUpload,
  label = 'Upload file',
  accept,
}) => {
  const [isUploading, setIsUploading] = useState(false)
  const [progress, setProgress] = useState<UploadProgress | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [urlInput, setUrlInput] = useState(currentUrl || '')
  const [inputMode, setInputMode] = useState<'upload' | 'url'>('upload')
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const getAcceptString = () => {
    if (accept) return accept
    switch (type) {
      case 'video':
        return 'video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.avi,.mkv'
      case 'document':
        return '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,application/pdf'
      case 'image':
        return 'image/*,.jpg,.jpeg,.png,.gif,.webp'
      default:
        return '*/*'
    }
  }

  const getMaxSize = () => {
    switch (type) {
      case 'video':
        return MAX_VIDEO_SIZE
      case 'document':
        return MAX_DOCUMENT_SIZE
      case 'image':
        return MAX_IMAGE_SIZE
      default:
        return MAX_DOCUMENT_SIZE
    }
  }

  const validateFile = (file: File): string | null => {
    switch (type) {
      case 'video':
        if (!isValidVideoFile(file)) {
          return 'Invalid video format. Allowed: MP4, WebM, MOV, AVI, MKV'
        }
        break
      case 'document':
        if (!isValidDocumentFile(file)) {
          return 'Invalid document format. Allowed: PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX, TXT'
        }
        break
      case 'image':
        if (!isValidImageFile(file)) {
          return 'Invalid image format. Allowed: JPG, PNG, GIF, WebP'
        }
        break
    }

    const maxSize = getMaxSize()
    if (file.size > maxSize) {
      return `File too large. Maximum size: ${formatFileSize(maxSize)}`
    }

    return null
  }

  const processFile = async (file: File) => {
    const validationError = validateFile(file)
    if (validationError) {
      setError(validationError)
      return
    }

    setError(null)
    setIsUploading(true)
    setProgress({ loaded: 0, total: file.size, percentage: 0 })

    try {
      let response
      switch (type) {
        case 'video':
          response = await uploadService.uploadVideo(file, setProgress)
          break
        case 'document':
          response = await uploadService.uploadDocument(file, setProgress)
          break
        case 'image':
          response = await uploadService.uploadImage(file, setProgress)
          break
        default:
          throw new Error('Invalid upload type')
      }

      if (response.success && response.data) {
        onUpload(response.data.url)
        setUrlInput(response.data.url)
        setUploadedFileName(file.name)
      } else {
        throw new Error(response.error?.message || 'Upload failed')
      }
    } catch (err) {
      console.error('Upload error:', err)
      setError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setIsUploading(false)
      setProgress(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) processFile(file)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) processFile(file)
  }

  const handleRemoveFile = () => {
    onUpload('')
    setUrlInput('')
    setUploadedFileName(null)
    setError(null)
  }

  const handleUrlSubmit = () => {
    if (urlInput.trim()) {
      onUpload(urlInput.trim())
      setUploadedFileName(null)
    }
  }

  const getIcon = () => {
    switch (type) {
      case 'video':
        return 'videocam'
      case 'document':
        return 'description'
      case 'image':
        return 'image'
      default:
        return 'upload_file'
    }
  }

  const hasUploadedFile = uploadedFileName || currentUrl

  return (
    <div className="space-y-3">
      {/* Mode Toggle */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setInputMode('upload')}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            inputMode === 'upload'
              ? 'bg-primary text-black'
              : 'bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          Upload File
        </button>
        <button
          type="button"
          onClick={() => setInputMode('url')}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            inputMode === 'url'
              ? 'bg-primary text-black'
              : 'bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          Paste URL
        </button>
      </div>

      {inputMode === 'upload' ? (
        isUploading && progress ? (
          <div className="border-2 border-primary bg-primary/5 rounded-lg p-6 text-center">
            <div className="space-y-3">
              <div className="size-12 rounded-full bg-primary/20 mx-auto flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
              <div className="text-sm text-gray-900 dark:text-white font-medium">Uploading... {progress.percentage}%</div>
              <div className="w-full bg-gray-100 dark:bg-white/10 rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{ width: `${progress.percentage}%` }}
                />
              </div>
              <div className="text-xs text-gray-500 dark:text-text-muted">
                {formatFileSize(progress.loaded)} / {formatFileSize(progress.total)}
              </div>
            </div>
          </div>
        ) : hasUploadedFile ? (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/20">
            <div className="size-10 rounded-lg bg-green-100 dark:bg-green-500/20 flex items-center justify-center shrink-0">
              <span className="material-symbols-outlined text-green-600 dark:text-green-400">check_circle</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-green-800 dark:text-green-300 font-medium truncate">
                {uploadedFileName || 'File berhasil diupload'}
              </p>
              <p className="text-xs text-green-600 dark:text-green-400">Berhasil diupload</p>
            </div>
            <button
              type="button"
              onClick={handleRemoveFile}
              className="p-2 rounded-lg hover:bg-green-100 dark:hover:bg-green-500/20 text-green-600 dark:text-green-400 hover:text-red-400 transition-colors"
              title="Ganti file"
            >
              <span className="material-symbols-outlined text-[18px]">close</span>
            </button>
          </div>
        ) : (
          <div
            className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              isDragOver
                ? 'border-primary bg-primary/5'
                : 'border-gray-200 dark:border-white/10 hover:border-gray-300 dark:border-white/20 bg-gray-100 dark:bg-white/5'
            }`}
            onDragOver={(e) => { e.preventDefault(); setIsDragOver(true) }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={getAcceptString()}
              onChange={handleFileSelect}
              disabled={isUploading}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
            />
            <div className="size-12 rounded-full bg-gray-100 dark:bg-white/10 mx-auto mb-3 flex items-center justify-center">
              <span className="material-symbols-outlined text-2xl text-gray-500 dark:text-gray-400">{getIcon()}</span>
            </div>
            <p className="text-sm text-gray-900 dark:text-white font-medium mb-1">{label}</p>
            <p className="text-xs text-gray-500 dark:text-text-muted">
              Click or drag and drop (max {formatFileSize(getMaxSize())})
            </p>
          </div>
        )
      ) : (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder={
              type === 'video'
                ? 'https://youtube.com/watch?v=... or https://vimeo.com/...'
                : 'https://example.com/file.pdf'
            }
            className="flex-1 px-4 py-3 rounded-lg bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <button
            type="button"
            onClick={handleUrlSubmit}
            className="px-4 py-3 rounded-lg bg-primary text-black font-bold hover:bg-primary-hover transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">check</span>
          </button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 text-red-500 dark:text-red-400 text-sm bg-red-50 dark:bg-red-500/10 p-3 rounded-lg border border-red-200 dark:border-red-500/20">
          <span className="material-symbols-outlined text-[18px]">error</span>
          {error}
        </div>
      )}
    </div>
  )
}

export default FileUpload
